/**
 * @file Recalls reason value and label helpers.
 */

/**
 * Normalizes a Recalls reason value to unique reason ids.
 * @param {string|Array<string>|null|undefined} value Raw reason value.
 * @returns {Array<string>} Normalized reason ids.
 */
export function normalizeRecallReasonIds(value) {
    let rawValues = value;
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            rawValues = Array.isArray(parsed) ? parsed : value;
        } catch {
            rawValues = value;
        }
    }

    const values = Array.isArray(rawValues) ? rawValues : [rawValues];
    const seen = new Set();
    return values
        .map((entry) => String(entry ?? "").trim())
        .filter((entry) => {
            if (!entry || seen.has(entry)) {return false;}
            seen.add(entry);
            return true;
        });
}

/**
 * Serializes Recalls reason ids for hidden control mirrors.
 * @param {string|Array<string>|null|undefined} value Raw reason value.
 * @returns {string} JSON array mirror value.
 */
export function serializeRecallReasonIds(value) {
    return JSON.stringify(normalizeRecallReasonIds(value));
}

/**
 * Returns whether a reason value contains at least one reason.
 * @param {string|Array<string>|null|undefined} value Raw reason value.
 * @returns {boolean} True when at least one reason is selected.
 */
export function hasRecallReasons(value) {
    return normalizeRecallReasonIds(value).length > 0;
}

/**
 * Returns the labels for a Recalls reason value.
 * @param {string|Array<string>|null|undefined} value Raw reason value.
 * @param {Array<object>} options Reason config options.
 * @returns {Array<string>} Reason labels.
 */
export function getRecallReasonLabels(value, options = []) {
    const reasonOptions = Array.isArray(options) ? options : [];
    return normalizeRecallReasonIds(value).map((reasonId) => {
        const reason = reasonOptions.find((option) => option?.id === reasonId);
        const label = reason?.name || reason?.label || reasonId;
        return reason?.isActive === false ? `${label} (inactive)` : label;
    }).filter(Boolean);
}

/**
 * Returns a display label for Recalls reason ids.
 * @param {string|Array<string>|null|undefined} value Raw reason value.
 * @param {Array<object>} options Reason config options.
 * @returns {string} Display label.
 */
export function getRecallReasonDisplay(value, options = []) {
    return getRecallReasonLabels(value, options).join(", ");
}
